import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, ChevronRight, Shield } from 'lucide-react';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-gradient-to-b from-[#E3F2FD] to-white pt-16 pb-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-3xl mx-auto"
          >
            <div className="mx-auto w-16 h-16 bg-[#FFF0F3] rounded-full flex items-center justify-center mb-6">
              <Lock className="h-8 w-8 text-[#FF6B6B]" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-[#5E60CE] mb-6 font-display">Privacy Policy</h1>
            <p className="text-lg text-[#4F86F7] mb-6 font-display">Last Updated: April 3, 2025</p>
            <div className="flex items-center justify-center space-x-2 text-sm text-gray-500 font-display">
              <Link to="/" className="hover:text-[#5E60CE]">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Privacy Policy</span>
            </div>
          </motion.div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-[24px] shadow-lg p-6 sm:p-8 border border-[#E6EFFD]">
            <div className="prose prose-lg prose-headings:font-display prose-headings:text-[#5E60CE] prose-p:font-display prose-p:text-gray-600 prose-a:text-[#4F86F7] prose-a:font-display max-w-none">
              <p>
                At kuchipuchi, we're committed to protecting your privacy and the privacy of children. This Privacy Policy explains how we collect, use, and share information in relation to our website, mobile applications, and services (collectively, the "Service").
              </p>
              
              <h2>1. Information We Collect</h2>
              
              <h3>1.1 Information You Provide</h3>
              <p>
                To create personalized stories, we may collect information such as:
              </p>
              <ul>
                <li>Account information (name, email address)</li>
                <li>Profile information for story personalization (child's name, age, interests)</li>
                <li>Payment information (processed securely through third-party payment processors)</li>
                <li>Content you create, upload, or provide (story preferences, custom elements)</li>
              </ul>
              
              <h3>1.2 Automatically Collected Information</h3>
              <p>
                When you use our Service, we may automatically collect certain information, including:
              </p>
              <ul>
                <li>Device information (device type, operating system, browser type)</li>
                <li>Usage information (how you interact with our Service)</li>
                <li>Log data (IP address, access times, pages viewed)</li>
                <li>Cookies and similar technologies</li>
              </ul>
              
              <h2>2. How We Use Your Information</h2>
              <p>
                We use the information we collect to:
              </p>
              <ul>
                <li>Provide, maintain, and improve our Service</li>
                <li>Create personalized stories and content</li>
                <li>Process transactions and send related information</li>
                <li>Send you technical notices, updates, and support messages</li>
                <li>Respond to your comments and questions</li>
                <li>Develop new features and services</li>
                <li>Monitor and analyze trends and usage</li>
              </ul>
              
              <h2>3. Information Sharing and Disclosure</h2>
              <p>
                We may share information as follows:
              </p>
              <ul>
                <li>With service providers who perform services on our behalf</li>
                <li>In response to legal requirements</li>
                <li>To protect rights, privacy, safety or property</li>
                <li>In connection with a merger, sale, or acquisition</li>
              </ul>
              <p>
                We do not sell or rent your personal information to third parties for their marketing purposes.
              </p>
              
              <h2>4. Children's Privacy</h2>
              <p>
                kuchipuchi is designed for parents, guardians, and educators to create stories for children. We do not knowingly collect personal information directly from children under 13. If you are a parent or guardian and believe your child has provided us with personal information, please contact us so we can delete it.
              </p>
              
              <h2>5. Data Retention</h2>
              <p>
                We retain information as long as necessary to provide our Service and for legitimate business purposes. If you delete your account, we will delete or anonymize your information, unless we need to retain certain information for legal reasons.
              </p>
              
              <h2>6. Security</h2>
              <p>
                We use reasonable security measures to protect your information. However, no method of transmission over the Internet or electronic storage is 100% secure, so we cannot guarantee absolute security.
              </p>
              
              <h2>7. Your Rights and Choices</h2>
              <p>
                Depending on your location, you may have certain rights regarding your personal information, including:
              </p>
              <ul>
                <li>Accessing, correcting, or deleting your information</li>
                <li>Objecting to certain processing activities</li>
                <li>Requesting a copy of your information</li>
                <li>Withdrawing consent</li>
              </ul>
              
              <h2>8. International Data Transfers</h2>
              <p>
                Your information may be transferred to, and processed in, countries other than your residence. These countries may have different data protection laws than your country.
              </p>
              
              <h2>9. Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date.
              </p>
              
              <h2>10. Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy, please contact us at:
              </p>
              <p>
              <a href="mailto:privacy@kuchipuchi.com">privacy@kuchipuchi.com</a>
              </p>
            </div>
          </div>
          
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-12 p-6 bg-[#F0F7FF] rounded-[24px] border border-[#E6EFFD] shadow-md"
          >
            <div className="flex items-start gap-4">
              <div className="mt-1 bg-white p-2 rounded-full">
                <Shield className="h-6 w-6 text-[#5E60CE]" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-[#5E60CE] font-display mb-2">Your Child's Privacy Matters</h3>
                <p className="text-gray-600 font-display">
                  We take children's privacy extremely seriously. If you have any concerns or questions about how we handle children's data, please <a href="mailto:privacy@kuchipuchi.com" className="text-[#4F86F7] font-bold hover:underline">contact us</a> directly.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Simple decorated footer */}
      <div className="py-10 bg-[#F8FBFF] border-t border-[#E6EFFD]">
        <div className="container mx-auto px-4 text-center">
          <p className="text-gray-500 font-display mb-4">kuchipuchi — Protecting your privacy while creating magic</p>
          <div className="w-20 h-1 bg-gradient-to-r from-[#FF6B6B] to-[#FF9AA2] mx-auto rounded-full"></div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy; 